import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import "./ui.css";

/** Page wrapper giving access to the UI kit styles. */
export const Page = ({ children }) => (
  <div className="container-fluid ui-page">{children}</div>
);

export const PageHeader = ({ title, subtitle, actions }) => (
  <header className="ui-page-header">
    <div>
      <h1 className="ui-page-title">{title}</h1>
      {subtitle && <p className="ui-page-subtitle">{subtitle}</p>}
    </div>
    {actions && <div className="ui-page-actions">{actions}</div>}
  </header>
);

export const SearchInput = ({ value, onChange, placeholder }) => (
  <label className="ui-search">
    <i className="ri-search-line" aria-hidden="true"></i>
    <input
      type="search"
      value={value}
      placeholder={placeholder}
      aria-label={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  </label>
);

/** Single-choice button group. `options`: [{ value, label }]. */
export const Segmented = ({ options, value, onChange, label }) => (
  <div className="ui-segmented" role="group" aria-label={label}>
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        aria-pressed={value === option.value}
        disabled={option.disabled}
        title={option.title}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </button>
    ))}
  </div>
);

export const COLORS = {
  blue: "var(--ui-blue)",
  green: "var(--ui-green)",
  amber: "var(--ui-amber)",
  violet: "var(--ui-violet)",
  red: "var(--ui-red)",
  slate: "var(--ui-slate)",
};

export const Badge = ({ color = "slate", dot, children }) => (
  <span
    className="ui-badge"
    data-dot={dot ? "" : undefined}
    style={{ "--badge-color": COLORS[color] || color }}
  >
    {children}
  </span>
);

/** Read-only value (URL, key…) with a button copying it. */
export const CopyField = ({ id, label, value }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = () =>
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      })
      .catch(() => {});
  return (
    <div className="ui-field">
      <label htmlFor={id}>{label}</label>
      <div className="ui-copy">
        <input id={id} className="ui-input" readOnly value={value || ""} />
        <button
          type="button"
          className="ui-btn ui-btn-secondary"
          onClick={copy}
          disabled={!value}
        >
          <i
            className={copied ? "ri-check-line" : "ri-file-copy-line"}
            aria-hidden="true"
          ></i>
          {copied ? t("Copied") : t("Copy")}
        </button>
      </div>
    </div>
  );
};

/**
 * Table row opening the details of its item when clicked: the page `to`, or
 * `onOpen()`. With `to`, its title should also be a link, for the keyboard
 * and new tabs.
 */
export const ClickableRow = ({ to, onOpen, selected, children }) => {
  const history = useHistory();
  return (
    <tr
      className={`ui-row-link${selected ? " is-selected" : ""}`}
      onClick={onOpen || (() => history.push(to))}
    >
      {children}
    </tr>
  );
};

/** Actions cell of a `ClickableRow`: its buttons do not open the details. */
export const ActionsCell = ({ children }) => (
  <td className="ui-cell-actions" onClick={(e) => e.stopPropagation()}>
    {children}
  </td>
);

export const IconButton = ({ icon, label, tone, onClick }) => (
  <button
    type="button"
    className="ui-icon-btn"
    data-tone={tone}
    title={label}
    aria-label={label}
    onClick={onClick}
  >
    <i className={icon} aria-hidden="true"></i>
  </button>
);

export const EmptyState = ({ icon = "ri-inbox-line", children }) => (
  <div className="ui-empty">
    <i className={icon} aria-hidden="true"></i>
    {children}
  </div>
);

/** Placeholder rows while a table loads. */
export const TableSkeleton = ({ rows = 6 }) => (
  <div style={{ padding: "8px 20px 20px" }}>
    {Array.from({ length: rows }, (_, i) => (
      <div
        key={i}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "12px 0",
        }}
      >
        <div className="ui-skeleton" style={{ width: 72, height: 42 }} />
        <div style={{ flex: 1 }}>
          <div
            className="ui-skeleton"
            style={{ width: "40%", height: 14, marginBottom: 8 }}
          />
          <div className="ui-skeleton" style={{ width: "20%", height: 12 }} />
        </div>
      </div>
    ))}
  </div>
);

/**
 * Client-side pagination of `items`. Goes back to page 1 whenever one of
 * `resetKeys` changes (e.g. the search text) and stays in range when items shrink.
 */
export function usePagination(items, pageSize = 10, resetKeys = []) {
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil((items?.length || 0) / pageSize));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setPage(1), resetKeys);
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const pageItems = useMemo(
    () => (items || []).slice((page - 1) * pageSize, page * pageSize),
    [items, page, pageSize]
  );
  return {
    page,
    setPage,
    pageCount,
    pageItems,
    total: items?.length || 0,
    pageSize,
  };
}

/** Page numbers to show: first, last, current ±1, with "…" gaps. */
export function pageNumbers(page, pageCount) {
  const pages = new Set([1, pageCount, page - 1, page, page + 1]);
  const sorted = [...pages]
    .filter((p) => p >= 1 && p <= pageCount)
    .sort((a, b) => a - b);
  const result = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) result.push("gap-" + p);
    result.push(p);
  });
  return result;
}

/**
 * Page navigation. With `total`, shows "1–10 of N" and page numbers; without it
 * (e.g. server-side search results), shows "Page N" with previous/next, next
 * being enabled by `hasNext`.
 */
export const Pagination = ({
  page,
  pageCount,
  total,
  pageSize,
  onChange,
  hasNext,
}) => {
  const { t } = useTranslation();
  const knownTotal = typeof total === "number";
  if (knownTotal && total === 0) return null;
  const lastPage = knownTotal ? pageCount : hasNext ? page + 1 : page;

  return (
    <nav className="ui-pagination" aria-label={t("Pagination")}>
      <span>
        {knownTotal
          ? `${(page - 1) * pageSize + 1}–${Math.min(
              page * pageSize,
              total
            )} ${t("of")} ${total}`
          : `${t("Page")} ${page}`}
      </span>
      {lastPage > 1 && (
        <div className="ui-pages">
          <button
            type="button"
            disabled={page === 1}
            onClick={() => onChange(page - 1)}
            aria-label={t("Previous")}
          >
            <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
          </button>
          {knownTotal &&
            pageNumbers(page, pageCount).map((p) =>
              typeof p === "string" ? (
                <span key={p} className="ui-pages-gap">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  type="button"
                  aria-current={p === page ? "page" : undefined}
                  onClick={() => onChange(p)}
                >
                  {p}
                </button>
              )
            )}
          <button
            type="button"
            disabled={page >= lastPage}
            onClick={() => onChange(page + 1)}
            aria-label={t("Next")}
          >
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          </button>
        </div>
      )}
    </nav>
  );
};

const CONTENT_TYPE_COLORS = {
  free: "green",
  subscription: "blue",
  exclusive: "amber",
};

/** Free / Subscription / Exclusive content badge. */
export const ContentTypeBadge = ({ type }) => {
  const { t } = useTranslation();
  if (!type) return null;
  return (
    <Badge color={CONTENT_TYPE_COLORS[type]}>
      {t(type.charAt(0).toUpperCase() + type.slice(1))}
    </Badge>
  );
};

/** Active / Inactive status badge. */
export const StatusBadge = ({ active }) => {
  const { t } = useTranslation();
  return active ? (
    <Badge color="green" dot>
      {t("Active")}
    </Badge>
  ) : (
    <Badge color="slate" dot>
      {t("Inactive")}
    </Badge>
  );
};

/** On/off toggle. */
export const Switch = ({ checked, onChange, label, onLabel, offLabel }) => (
  <button
    type="button"
    role="switch"
    className="ui-switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
  >
    <span className="ui-switch-track" aria-hidden="true">
      <span className="ui-switch-thumb" />
    </span>
    {(onLabel || offLabel) && (
      <span className="ui-switch-text">{checked ? onLabel : offLabel}</span>
    )}
  </button>
);

/**
 * Button opening a list of checkboxes (multi-select). `options`: strings.
 * With `searchable`, a filter field is shown above the options.
 */
export const CheckboxDropdown = ({
  label,
  options,
  value,
  onChange,
  searchable,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const shown = query
    ? options.filter((o) => o.toLowerCase().includes(query.toLowerCase()))
    : options;
  const toggle = (option) =>
    onChange(
      value.includes(option)
        ? value.filter((v) => v !== option)
        : [...value, option]
    );

  return (
    <div className="ui-dropdown" ref={ref}>
      <button
        type="button"
        className="ui-input ui-dropdown-toggle"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span>
          {value.length === 0
            ? label
            : value.length === 1
            ? value[0]
            : `${label} · ${value.length}`}
        </span>
        <i className="ri-arrow-down-s-line" aria-hidden="true"></i>
      </button>
      {open && (
        <div className="ui-dropdown-menu">
          {searchable && (
            <input
              className="ui-input"
              type="search"
              value={query}
              placeholder={t("Search")}
              aria-label={t("Search")}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}
          <ul>
            {shown.map((option) => (
              <li key={option}>
                <label>
                  <input
                    type="checkbox"
                    checked={value.includes(option)}
                    onChange={() => toggle(option)}
                  />
                  {option}
                </label>
              </li>
            ))}
          </ul>
          {value.length > 0 && (
            <button
              type="button"
              className="ui-dropdown-clear"
              onClick={() => onChange([])}
            >
              {t("Clear")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/** Free-text tags: Enter adds the typed tag, × removes one. */
export const TagInput = ({ id, value, onChange, placeholder, disabled }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const add = () => {
    if (!draft.trim()) return;
    onChange([...value, draft]);
    setDraft("");
  };
  return (
    <div className="ui-tags" data-disabled={disabled ? "" : undefined}>
      {value.map((tag, index) => (
        <span key={index} className="ui-tag">
          {tag}
          {!disabled && (
            <button
              type="button"
              aria-label={`${t("Remove")} ${tag}`}
              onClick={() => onChange(value.filter((_, i) => i !== index))}
            >
              ×
            </button>
          )}
        </span>
      ))}
      <input
        id={id}
        value={draft}
        disabled={disabled}
        placeholder={value.length === 0 ? placeholder : ""}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            add();
          } else if (e.key === "Backspace" && !draft && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
      />
    </div>
  );
};
